/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/array/ArrayRemove.ts
 * @create:      2025-06-26 15:59:16.601
 * @modify:      2025-07-11 21:19:12.783
 * @version:     6.0.0
 * @times:       18
 * @lines:       120
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { TPredicateFunction, TRemoveType } from "../type/basic";

/**
 * Removes the specified item from the given array.
 *
 * @param array - The array from which to remove the item.
 * @param item - The item to be removed from the array.
 * @param type - (Optional) The removal strategy or type, defined by `TRemoveType`.
 * @returns The original array with the specified item removed.
 * 
 * @since 4.1.0
 * @version 2025-07-29
 */
export function ArrayRemove<T>(array: T[], item: T, type?: TRemoveType): T[];
/**
 * Removes all occurrences of the specified items from the given array.
 *
 * @param array - The array from which to remove items.
 * @param items - The items to remove from the array.
 * @returns The original array with the specified items removed.
 * 
 * @since 4.1.0
 * @version 2025-07-29
 */
export function ArrayRemove<T>(array: T[], items: T[]): T[];
/**
 * Removes elements from the array that match the given predicate function.
 *
 * @param array - The array to remove elements from.
 * @param predicate - A function that determines whether an element should be removed.
 * @returns The original array with the elements that do not match the predicate.
 * 
 * @since 4.1.0
 * @version 2025-07-29
 */
export function ArrayRemove<T>(array: T[], predicate: TPredicateFunction<T>): T[];
export function ArrayRemove<T>(array: T[], val: T | T[] | TPredicateFunction<T>, type?: TRemoveType): T[] {
    if (!Array.isArray(array) || array.length === 0) {
        return array;
    }

    let checkFunction: TPredicateFunction<T> | undefined;

    const indexSet = new Set<number>();
    if (typeof val === "function") {
        checkFunction = val as TPredicateFunction<T>;
    } else {
        if (Array.isArray(val)) {
            checkFunction = (item: T) => val.includes(item);
        } else {
            if (type === "all") {
                checkFunction = (item: T) => item === val;
            } else if (type === "right-one") {
                const index = array.lastIndexOf(val);
                if (index !== -1) {
                    indexSet.add(index);
                }
            } else {
                // default to "left-one"
                // This is the same as not specifying a type
                const index = array.indexOf(val);
                if (index !== -1) {
                    indexSet.add(index);
                }
            }
        }
    }

    if (checkFunction) {
        for (let i = 0; i < array.length; i++) {
            if (checkFunction(array[i])) {
                indexSet.add(i);
            }
        }
    }

    if (indexSet.size > 0) {
        const indexes = Array.from(indexSet);
        indexes.sort().reverse();

        for (const index of indexes) {
            array.splice(index, 1);
        }
    }

    return array;
}
